/**
 * Article Enhancements — ShoeZero
 * Progressive enhancement for article pages.
 * Page works without JS. This adds: TOC, progress bar, image zoom, copy link, table wrappers.
 * ~6KB unminified, loaded as deferred module on article pages only.
 */

(function () {
  'use strict';

  var SCROLL_OFFSET = 80;
  var HEADING_SELECTORS = { h2: 'h2', h2h3: 'h2' };

  document.addEventListener('DOMContentLoaded', init);

  function init() {
    document.documentElement.classList.add('js-loaded');
    initProgressBar();
    initTableOfContents();
    wrapKeyTakeaways();
    initScrollFadeIn();
    initBodyImageProcessing();
    initCopyLinkButtons();
    initTableWrappers();
    cleanGhostSpacers();
    initBrCleanup();
  }

  function debounce(fn, delay) {
    var timer;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () { fn.apply(ctx, args); }, delay);
    };
  }

  // ---- 0. Key Takeaways Wrapper ----
  function wrapKeyTakeaways() {
    var prose = document.querySelector('.prose');
    if (!prose) return;

    // Skip if metafield-rendered snippet already exists
    if (document.querySelector('.article-key-takeaways')) return;

    var headings = prose.querySelectorAll('h2, h3, h4');

    for (var i = 0; i < headings.length; i++) {
      var heading = headings[i];
      var text = heading.textContent.replace(/\s+/g, ' ').trim();

      if (!/^key\s+takeaways?\s*:?\s*$/i.test(text)) continue;
      if (heading.closest('.article-key-takeaways')) continue;

      // Collect siblings until next heading
      var contentEls = [];
      var sibling = heading.nextElementSibling;
      while (sibling) {
        if (/^H[1-6]$/.test(sibling.tagName)) break;
        contentEls.push(sibling);
        sibling = sibling.nextElementSibling;
      }
      if (contentEls.length === 0) continue;

      // Create wrapper
      var aside = document.createElement('aside');
      aside.className = 'article-key-takeaways';
      heading.parentNode.insertBefore(aside, heading);

      // Move heading + add class
      aside.appendChild(heading);
      heading.classList.add('article-key-takeaways__heading');

      // Move content + add classes
      for (var j = 0; j < contentEls.length; j++) {
        var el = contentEls[j];
        if (el.tagName === 'UL' || el.tagName === 'OL') {
          el.classList.add('article-key-takeaways__list');
        }
        aside.appendChild(el);
      }
      break; // Only wrap the first Key Takeaways per article
    }
  }

  // ---- 1. Reading Progress Bar ----
  function initProgressBar() {
    var articleEl = document.querySelector('.prose') || document.querySelector('.article-content');
    if (!articleEl) return;

    var bar = document.createElement('div');
    bar.className = 'article-progress-bar';
    bar.innerHTML = '<div class="article-progress-bar__fill"></div>';
    document.body.prepend(bar);

    var fill = bar.querySelector('.article-progress-bar__fill');
    var ticking = false;

    window.addEventListener('scroll', function () {
      if (!ticking) {
        requestAnimationFrame(function () {
          var rect = articleEl.getBoundingClientRect();
          var articleTop = rect.top + window.scrollY;
          var articleHeight = rect.height;
          var scrollPos = window.scrollY - articleTop;
          var maxScroll = articleHeight - window.innerHeight;
          var progress = Math.min(Math.max(scrollPos / maxScroll, 0), 1);
          fill.style.transform = 'scaleX(' + progress + ')';
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
  }

  // ---- 2. Table of Contents ----
  function initTableOfContents() {
    var tocContainer = document.querySelector('.article-toc');
    var sidebar = document.querySelector('.article-toc-sidebar');
    var trigger = document.querySelector('.article-toc-trigger');
    if (!tocContainer) return;

    var depth = tocContainer.getAttribute('data-toc-depth') || 'h2h3';
    var minHeadings = parseInt(tocContainer.getAttribute('data-toc-min') || '3', 10);
    var numbered = tocContainer.getAttribute('data-toc-numbered') === 'true';
    var selector = HEADING_SELECTORS[depth] || 'h2, h3';

    var prose = document.querySelector('.prose');
    if (!prose) return;

    var headings = prose.querySelectorAll(selector);
    if (headings.length < 2) {
      tocContainer.style.display = 'none';
      if (sidebar) sidebar.style.display = 'none';
      if (trigger) trigger.style.display = 'none';
      return;
    }
    if (headings.length < minHeadings) {
      tocContainer.style.display = 'none';
      if (sidebar) sidebar.style.display = 'none';
      if (trigger) trigger.style.display = 'none';
      return;
    }

    var nav = tocContainer.querySelector('.article-toc__nav');
    if (!nav) return;

    var list = document.createElement(numbered ? 'ol' : 'ul');
    var usedIds = {};
    var tocLinks = [];

    headings.forEach(function (heading) {
      var id = slugify(heading.textContent);
      if (usedIds[id]) {
        var counter = 2;
        while (usedIds[id + '-' + counter]) counter++;
        id = id + '-' + counter;
      }
      usedIds[id] = true;
      heading.id = id;

      var link = document.createElement('a');
      link.href = '#' + id;
      link.textContent = heading.textContent.trim();
      link.addEventListener('click', function (e) {
        e.preventDefault();
        var target = document.getElementById(id);
        if (target) {
          var top = target.getBoundingClientRect().top + window.scrollY - SCROLL_OFFSET;
          window.scrollTo({ top: top, behavior: 'smooth' });
          history.pushState(null, '', '#' + id);
        }
      });

      var li = document.createElement('li');
      li.appendChild(link);
      tocLinks.push({ link: link, li: li, id: id });

      list.appendChild(li);

    });

    nav.appendChild(list);

    // Desktop (≥1200px): force open, prevent closing
    var details = tocContainer.querySelector('details');
    function handleDetailsState() {
      if (!details) return;
      if (window.innerWidth >= 1200) {
        details.open = true;
        details.addEventListener('toggle', forceOpen);
      } else if (window.innerWidth >= 769) {
        details.removeEventListener('toggle', forceOpen);
        details.open = true;
      }
    }
    function forceOpen() {
      if (window.innerWidth >= 1200 && !details.open) {
        details.open = true;
      }
    }
    handleDetailsState();
    window.addEventListener('resize', debounce(handleDetailsState, 150));

    // Intersection Observer for active state
    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            tocLinks.forEach(function (item) {
              var isActive = item.id === entry.target.id;
              item.link.classList.toggle('is-active', isActive);
              item.li.classList.toggle('is-active-item', isActive);
            });

            // Scroll active link into view in sticky sidebar (sidebar only, never page)
            if (window.innerWidth >= 1200 && sidebar) {
              var activeLink = nav.querySelector('a.is-active');
              if (activeLink) {
                var linkTop = activeLink.offsetTop - sidebar.offsetTop;
                var sidebarScroll = sidebar.scrollTop;
                var sidebarHeight = sidebar.clientHeight;
                // Only scroll sidebar if active link is outside visible area
                if (linkTop < sidebarScroll || linkTop > sidebarScroll + sidebarHeight - 40) {
                  sidebar.scrollTop = linkTop - sidebarHeight / 2;
                }
              }
            }
          }
        });
      }, {
        rootMargin: '-' + SCROLL_OFFSET + 'px 0px -66% 0px',
        threshold: 0
      });

      headings.forEach(function (h) { observer.observe(h); });
    }
  }

  // ---- 2c. Scroll Fade-In ----
  function initScrollFadeIn() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var prose = document.querySelector('.prose');
    if (!prose || !('IntersectionObserver' in window)) return;

    var elements = prose.querySelectorAll('h2, h3, img, blockquote, table');
    var keyTakeaways = document.querySelector('.article-key-takeaways');

    var fadeObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          fadeObserver.unobserve(entry.target);
        }
      });
    }, {
      rootMargin: '0px 0px -50px 0px',
      threshold: 0.1
    });

    elements.forEach(function (el) { fadeObserver.observe(el); });
    if (keyTakeaways) fadeObserver.observe(keyTakeaways);
  }

  // ---- 3. Body Image Processing ----
  function initBodyImageProcessing() {
    var prose = document.querySelector('.prose');
    if (!prose) return;

    var images = prose.querySelectorAll('img');
    images.forEach(function (img) {
      // Add lazy loading if missing
      if (!img.hasAttribute('loading')) {
        img.setAttribute('loading', 'lazy');
      }

      // Click-to-zoom for large images
      if (img.naturalWidth > 600 || img.width > 400) {
        img.style.cursor = 'zoom-in';
        img.addEventListener('click', function () {
          openLightbox(img);
        });
      } else {
        // Wait for load to check natural width
        img.addEventListener('load', function () {
          if (img.naturalWidth > 600) {
            img.style.cursor = 'zoom-in';
            img.addEventListener('click', function () {
              openLightbox(img);
            });
          }
        });
      }
    });

    // Float classification: small images float right on desktop
    // Use window.load so all images are decoded and naturalWidth is available
    window.addEventListener('load', function () {
      classifyImagesForFloat(prose, images);
    });

    window.addEventListener('resize', debounce(function () {
      classifyImagesForFloat(prose, images);
    }, 250));
  }

  // ---- Image Float Classification ----
  var FLOAT_THRESHOLD = 0.50; // Fraction of .prose width; tune this one constant

  function classifyImagesForFloat(prose, images) {
    if (window.innerWidth < 750) return;
    var proseWidth = prose.clientWidth;
    if (proseWidth === 0) return;

    images.forEach(function (img) {
      // Skip images inside <figure> or <a> — floating breaks captions/links
      if (closestTag(img, 'FIGURE') || closestTag(img, 'A')) return;
      // Skip images explicitly set to 100% width
      var style = img.getAttribute('style') || '';
      if (/width\s*:\s*100%/.test(style)) return;

      var classify = function () {
        var colWidth = prose.clientWidth || proseWidth;
        var imgWidth = img.naturalWidth || parseInlineWidth(img, colWidth);

        // Can't measure — safe fallback: no float
        if (!imgWidth || !colWidth) {
          img.classList.remove('prose-img--float-right');
          return;
        }

        if (imgWidth < colWidth * FLOAT_THRESHOLD) {
          img.classList.add('prose-img--float-right');
        } else {
          img.classList.remove('prose-img--float-right');
        }
      };

      if (img.complete && img.naturalWidth > 0) {
        classify();
      } else {
        img.addEventListener('load', classify, { once: true });
        img.addEventListener('error', function () {
          img.classList.remove('prose-img--float-right');
        }, { once: true });
      }
    });
  }

  function closestTag(el, tag) {
    var node = el.parentElement;
    while (node && !node.classList.contains('prose')) {
      if (node.tagName === tag) return true;
      node = node.parentElement;
    }
    return false;
  }

  function parseInlineWidth(img, proseWidth) {
    var style = img.getAttribute('style') || '';
    var px = style.match(/width\s*:\s*(\d+(?:\.\d+)?)px/);
    if (px) return parseFloat(px[1]);
    var pct = style.match(/width\s*:\s*(\d+(?:\.\d+)?)%/);
    if (pct) return (parseFloat(pct[1]) / 100) * proseWidth;
    var attr = img.getAttribute('width');
    if (attr) return parseFloat(attr);
    return 0;
  }

  function openLightbox(img) {
    var dialog = document.createElement('dialog');
    dialog.className = 'article-lightbox';

    var closeBtn = document.createElement('button');
    closeBtn.className = 'article-lightbox__close';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.innerHTML = '&times;';

    var fullImg = document.createElement('img');
    // Use Shopify CDN to get larger version
    var src = img.src;
    if (src.indexOf('width=') !== -1) {
      src = src.replace(/width=\d+/, 'width=2000');
    }
    fullImg.src = src;
    fullImg.alt = img.alt || '';

    dialog.appendChild(closeBtn);
    dialog.appendChild(fullImg);
    document.body.appendChild(dialog);
    dialog.showModal();

    closeBtn.addEventListener('click', function () {
      dialog.close();
    });
    dialog.addEventListener('click', function (e) {
      if (e.target === dialog) dialog.close();
    });
    dialog.addEventListener('close', function () {
      dialog.remove();
    });
    document.addEventListener('keydown', function handler(e) {
      if (e.key === 'Escape') {
        dialog.close();
        document.removeEventListener('keydown', handler);
      }
    });
  }

  // ---- 4. Copy Link Buttons ----
  function initCopyLinkButtons() {
    var buttons = document.querySelectorAll('[data-copy-link]');
    buttons.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var url = window.location.href;
        if (navigator.clipboard) {
          navigator.clipboard.writeText(url).then(function () {
            showCopiedFeedback(btn);
          });
        } else {
          // Fallback
          var textarea = document.createElement('textarea');
          textarea.value = url;
          textarea.style.position = 'fixed';
          textarea.style.opacity = '0';
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand('copy');
          document.body.removeChild(textarea);
          showCopiedFeedback(btn);
        }
      });
    });
  }

  function showCopiedFeedback(btn) {
    var tooltip = btn.querySelector('.share-buttons__copied-tooltip');
    if (!tooltip) {
      tooltip = document.createElement('span');
      tooltip.className = 'share-buttons__copied-tooltip';
      tooltip.textContent = 'Copied!';
      btn.style.position = 'relative';
      btn.appendChild(tooltip);
    }
    tooltip.classList.add('is-visible');
    setTimeout(function () {
      tooltip.classList.remove('is-visible');
    }, 2000);
  }

  // ---- 5. Table Overflow Wrappers ----
  function initTableWrappers() {
    var prose = document.querySelector('.prose');
    if (!prose) return;

    var tables = prose.querySelectorAll('table');
    tables.forEach(function (table) {
      if (table.parentElement.classList.contains('table-overflow-wrapper')) return;
      var wrapper = document.createElement('div');
      wrapper.className = 'table-overflow-wrapper';
      table.parentNode.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    });
  }

  // ---- 9. Ghost Spacer Cleanup ----
  function cleanGhostSpacers() {
    var prose = document.querySelector('.prose');
    if (!prose) return;
    var paras = prose.querySelectorAll('p');
    for (var i = 0; i < paras.length; i++) {
      var p = paras[i];
      if (!p.textContent.trim() && !p.querySelector('img, video, iframe, svg')) {
        p.style.display = 'none';
      }
    }
  }

  // ---- 6. BR Cleanup ----
  function initBrCleanup() {
    var prose = document.querySelector('.prose');
    if (!prose) return;
    var brs = prose.querySelectorAll('br + br');
    brs.forEach(function (br) {
      br.parentNode.removeChild(br);
    });
  }

  // ---- Utility ----
  function slugify(text) {
    return text
      .toString()
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '-')
      .replace(/[^\w-]/g, '')
      .replace(/--+/g, '-')
      .replace(/^-+|-+$/g, '');
  }
})();
